import { useEffect, useState } from 'react'
import { Check, Copy } from 'lucide-react'
import { cn } from '@/lib/utils'
import { buttonClass, type ButtonSize } from './Button'
import { useToast } from './Toast'

export interface CopyButtonProps {
  text: string
  /** Visible label; without one the button is an icon. */
  label?: string
  /** What was copied, for the screen reader and the failure toast: "본문". */
  what?: string
  size?: ButtonSize
  className?: string
}

/** Copies `text` and shows a check for a moment. */
export function CopyButton({ text, label, what = '내용', size = 'sm', className }: CopyButtonProps) {
  const [done, setDone] = useState(false)
  const toast = useToast()
  useEffect(() => {
    if (!done) return
    const t = window.setTimeout(() => setDone(false), 1500)
    return () => window.clearTimeout(t)
  }, [done])

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(text)
      setDone(true)
    } catch (e) {
      toast.error(`${what}을(를) 복사하지 못했다: ${e instanceof Error ? e.message : String(e)}`)
    }
  }
  const Icon = done ? Check : Copy
  return (
    <button
      type="button"
      onClick={copy}
      aria-label={label ? undefined : `${what} 복사`}
      title={label ? undefined : `${what} 복사`}
      className={cn(buttonClass('secondary', size), !label && (size === 'sm' ? 'w-8 px-0' : 'w-9 px-0'), className)}
    >
      <Icon size={14} className={done ? 'text-status-published' : undefined} aria-hidden />
      {label && <span>{done ? '복사했다' : label}</span>}
    </button>
  )
}
